import axios from 'axios';
import { downloadFeesSummaryPdf, uploadFeesSummaryToS3 } from '../../components/utility/feesSummaryPdf';
import { feesFormDataFromRecord } from '../../components/utility/feesFormDataFromRecord';

const SERVER_URL = process.env.REACT_APP_API_URL;

// Error bodies come back as JSON, but download requests ask for a blob, so the
// JSON has to be read out of the blob before the message is usable.
const readErrorMessage = async (e) => {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error || parsed?.Error || parsed?.message;
    } catch {
      return undefined;
    }
  }
  return data?.error || data?.Error || data?.message;
};

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

export const PastTransactionsApi = {
  // product: 'fees' | 'docs' | 'lien_title'
  list: async (product) => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/past-transactions`, {
        params: { product },
        withCredentials: true,
      });
      return { transactions: response.data?.transactions || [] };
    } catch (e) {
      return {
        error: true,
        message: (await readErrorMessage(e)) || 'Could not load past transactions.',
        status: e?.response?.status || 500,
      };
    }
  },

  // Fees transaction with no summary PDF in S3: rebuild it in the browser from
  // the quote stored with the transaction, save it, and upload it so the next
  // download comes straight from S3.
  rebuildFeesSummary: async (transactionId) => {
    try {
      const { data } = await axios.get(
        `${SERVER_URL}/api/past-transactions/fees/${encodeURIComponent(transactionId)}/report-data`,
        { withCredentials: true }
      );
      const response = data?.response || {};
      const formData = feesFormDataFromRecord(response.Inputs, transactionId);
      if (!formData.vinCode && data?.vin) formData.vinCode = data.vin;
      await downloadFeesSummaryPdf(formData, response);
      uploadFeesSummaryToS3(formData, response);
      return { status: 200 };
    } catch (e) {
      return {
        error: true,
        message: (await readErrorMessage(e)) || 'The summary PDF for this transaction is not available.',
        status: e?.response?.status || 500,
      };
    }
  },

  download: async (product, transactionId) => {
    try {
      const response = await axios.get(
        `${SERVER_URL}/api/past-transactions/${product}/${encodeURIComponent(transactionId)}/download`,
        { withCredentials: true, responseType: 'blob' }
      );
      const extension = product === 'docs' ? 'zip' : 'pdf';
      saveBlob(response.data, `${transactionId}.${extension}`);
      return { status: 200 };
    } catch (e) {
      if (product === 'fees' && e?.response?.status === 404) {
        return PastTransactionsApi.rebuildFeesSummary(transactionId);
      }
      return {
        error: true,
        message: (await readErrorMessage(e)) || 'Download failed. Please try again.',
        status: e?.response?.status || 500,
      };
    }
  },
};
